import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ShieldCheckIcon, AlertTriangleIcon, FileTextIcon, VideoIcon, ClipboardListIcon, CalendarIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const syncPatterns = [{
  pattern: 'One-Way (NIPR → JWICS)',
  useCase: 'Organizational data, base user attributes',
  method: 'Removable media, Guard solution',
  frequency: 'Daily/Weekly'
}, {
  pattern: 'One-Way (JWICS → NIPR)',
  useCase: 'Aggregated metrics (sanitized)',
  method: 'Manual export after classification review',
  frequency: 'Monthly'
}, {
  pattern: 'No Sync',
  useCase: 'Operational data, mission context',
  method: 'Maintain separately',
  frequency: 'N/A'
}, {
  pattern: 'Bi-Directional (rare)',
  useCase: 'Non-sensitive config data',
  method: 'Complex approval process',
  frequency: 'As needed'
}];
export function NIPRJWICSPage() {
  return <div className="w-full" data-id="element-551">
      {/* HERO */}
      <section className="bg-nrs-navy hero-grid-pattern py-20 px-4" data-id="element-552">
        <div className="max-w-7xl mx-auto" data-id="element-553">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl" data-id="element-554">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-6" data-id="element-555">
              <ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-556" />
              TS/SCI Cleared Team | IC/DoD Specialists
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-white leading-tight mb-6" data-id="element-557">
              Most ServiceNow Consultants Can't Touch Your Classified
              Environment. <span className="text-nrs-orange" data-id="element-558">We Can.</span>
            </h1>
            <p className="text-blue-200 text-lg leading-relaxed mb-8 max-w-3xl" data-id="element-559">
              For Intelligence Community and DoD agencies operating ServiceNow
              across classification domains (NIPR/JWICS/SIPR), cross-domain data
              synchronization is one of the most painful, risky, and expensive
              challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-560">
              <a href="mailto:info@newriversystems.com" className="px-6 py-3 bg-nrs-orange text-white font-semibold rounded-md hover:bg-amber-500 transition-colors text-center" data-id="element-561">
                Schedule Cross-Domain Assessment
              </a>
              <a href="#" className="px-6 py-3 border-2 border-white text-white font-semibold rounded-md hover:bg-white hover:text-nrs-navy transition-colors text-center" data-id="element-562">
                Download White Paper
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-white py-16 px-4" data-id="element-563">
        <div className="max-w-7xl mx-auto" data-id="element-564">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-565">
            <div data-id="element-566">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-6" data-id="element-567">
                The Problem
              </h2>
              <p className="text-gray-600 text-sm mb-6" data-id="element-568">
                Most agencies operating ServiceNow in classified environments
                face:
              </p>
              <div className="space-y-3" data-id="element-569">
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-570">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-571" />
                  <div data-id="element-572">
                    <strong data-id="element-573">Manual Data Replication</strong> — Admins manually
                    re-key data from NIPR to JWICS (high error rate, labor
                    intensive)
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-574">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-575" />
                  <div data-id="element-576">
                    <strong data-id="element-577">Data Divergence</strong> — NIPR and JWICS instances
                    fall out of sync, causing operational confusion
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-578">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-579" />
                  <div data-id="element-580">
                    <strong data-id="element-581">Security Risks</strong> — Inconsistent data between
                    domains creates accidental spillage opportunities
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-582">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-583" />
                  <div data-id="element-584">
                    <strong data-id="element-585">No Standard Patterns</strong> — Each agency
                    reinvents cross-domain sync (no best practices)
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700 bg-red-50 rounded-lg p-3 border border-red-100" data-id="element-586">
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-587" />
                  <div data-id="element-588">
                    <strong data-id="element-589">Lack of Cleared Consultants</strong> — Most
                    ServiceNow SIs can't access classified environments to help
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-590">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-6" data-id="element-591">
                Why This Is Hard
              </h2>
              <div className="space-y-4" data-id="element-592">
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-593">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-594">
                    Air-Gapped Networks
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-595">
                    NIPR and JWICS cannot directly connect (security by design)
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-596">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-597">
                    Classification Challenges
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-598">
                    Determining what data CAN sync vs. what must stay
                    domain-specific
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-599">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-600">
                    Data Governance Complexity
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-601">
                    Master data ownership unclear across domains
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-602">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-603">
                    RMF/ATO Impact
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-604">
                    Cross-domain sync changes require re-authorization
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-605">
                  <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-606">
                    Vendor Access
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-607">
                    Most consultants lack TS/SCI clearances to work in JWICS
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW NRS SOLVES THIS */}
      <section className="bg-nrs-light py-16 px-4" data-id="element-608">
        <div className="max-w-7xl mx-auto" data-id="element-609">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-610">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-3" data-id="element-611">
              How NRS Solves This
            </h2>
            <p className="text-gray-600" data-id="element-612">
              Our Cross-Domain Data Governance Framework
            </p>
          </motion.div>

          <div className="space-y-6" data-id="element-613">
            {[{
            step: '01',
            title: 'Classification Triage',
            desc: 'Identify which data domains MUST sync (users, organizational structure, CIs) and which CANNOT sync (operational details, mission-specific context). Create classification matrix for each ServiceNow table.'
          }, {
            step: '02',
            title: 'Master Data Ownership',
            desc: 'Designate NIPR or JWICS as "System of Record" for each data domain. Example: NIPR is SoR for organizational structure; JWICS is SoR for classified assets. Document in Data Governance Charter.'
          }, {
            step: '03',
            title: 'Sync Pattern Selection',
            desc: 'Based on classification and direction, choose the appropriate sync pattern. See the pattern matrix below.'
          }, {
            step: '04',
            title: 'Data Quality Monitoring',
            desc: 'Track sync success rate (target: >98%), monitor data discrepancy rate between domains (target: <2%), and alert on critical failures (user missing in JWICS but present in NIPR).'
          }, {
            step: '05',
            title: 'Security & ATO Integration',
            desc: 'Pre-approved sync patterns documented in RMF package. Change management process for new data types. Regular audits to prevent classification violations.'
          }].map((item, i) => <motion.div key={item.step} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl p-6 flex gap-5 items-start border border-gray-100 shadow-sm" data-id="element-614">
                <div className="flex-shrink-0 w-12 h-12 bg-nrs-blue rounded-full flex items-center justify-center" data-id="element-615">
                  <span className="text-white font-heading font-bold text-sm" data-id="element-616">
                    {item.step}
                  </span>
                </div>
                <div data-id="element-617">
                  <h3 className="font-heading font-bold text-base text-nrs-navy mb-2" data-id="element-618">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-619">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Sync Pattern Table */}
          <div className="mt-10 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-620">
            <div className="bg-nrs-navy px-6 py-4" data-id="element-621">
              <h3 className="font-heading font-bold text-white text-base" data-id="element-622">
                Sync Pattern Matrix
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-623">
              <table className="w-full text-sm" data-id="element-624">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-625">
                  <tr data-id="element-626">
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-627">
                      Pattern
                    </th>
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-628">
                      Use Case
                    </th>
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-629">
                      Method
                    </th>
                    <th className="px-4 py-3 text-left font-heading font-semibold text-gray-700 text-xs uppercase" data-id="element-630">
                      Frequency
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-631">
                  {syncPatterns.map((row, i) => <tr key={row.pattern} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-632">
                      <td className="px-4 py-3 font-semibold text-nrs-navy text-xs" data-id="element-633">
                        {row.pattern}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs" data-id="element-634">
                        {row.useCase}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs" data-id="element-635">
                        {row.method}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs" data-id="element-636">
                        {row.frequency}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* COMPETITIVE ADVANTAGE */}
      <section className="bg-nrs-navy py-16 px-4" data-id="element-637">
        <div className="max-w-7xl mx-auto" data-id="element-638">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-639">
            <div data-id="element-640">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-6" data-id="element-641">
                NRS's Competitive Advantage
              </h2>
              <div className="space-y-4" data-id="element-642">
                <div className="flex items-start gap-3" data-id="element-643">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-644" />
                  <div data-id="element-645">
                    <div className="text-white font-semibold text-sm" data-id="element-646">
                      TS/SCI Cleared Team
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-647">
                      We can actually access your JWICS environment
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-648">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-649" />
                  <div data-id="element-650">
                    <div className="text-white font-semibold text-sm" data-id="element-651">
                      IC Experience
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-652">
                      We've implemented cross-domain sync for DIA, NSA, and
                      other agencies
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-653">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-654" />
                  <div data-id="element-655">
                    <div className="text-white font-semibold text-sm" data-id="element-656">
                      Proven Patterns
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-657">
                      We have documented, ATO-approved sync architectures
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-658">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-659" />
                  <div data-id="element-660">
                    <div className="text-white font-semibold text-sm" data-id="element-661">
                      Data Governance Expertise
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-662">
                      We know how to establish System of Record and master data
                      ownership
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-663">
                  <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-664" />
                  <div data-id="element-665">
                    <div className="text-white font-semibold text-sm" data-id="element-666">
                      RMF/ATO Fluency
                    </div>
                    <div className="text-blue-200 text-xs mt-0.5" data-id="element-667">
                      We understand how cross-domain sync impacts your security
                      posture
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Study Snapshot */}
            <div className="bg-white/10 rounded-xl p-6 border border-white/20" data-id="element-668">
              <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-3" data-id="element-669">
                Case Study Snapshot
              </div>
              <h3 className="font-heading font-bold text-white text-lg mb-4" data-id="element-670">
                Large IC Organization
              </h3>
              <div className="text-blue-200 text-xs mb-4" data-id="element-671">
                8,000 NIPR users + 3,000 JWICS users
              </div>

              <div className="space-y-3 mb-5" data-id="element-672">
                <div className="font-heading font-semibold text-white text-xs uppercase tracking-wider" data-id="element-673">
                  Challenge
                </div>
                <div className="space-y-1 text-sm text-blue-200" data-id="element-674">
                  <div data-id="element-675">• 40 hours/week manual data replication</div>
                  <div data-id="element-676">• 15% error rate causing operational issues</div>
                  <div data-id="element-677">• No visibility into data divergence</div>
                  <div data-id="element-678">• ATO at risk due to undocumented sync</div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-4" data-id="element-679">
                <div className="font-heading font-semibold text-white text-xs uppercase tracking-wider mb-3" data-id="element-680">
                  Results
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-681">
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-682">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-683">
                      95%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-684">
                      Reduction in manual effort
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-685">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-686">
                      &lt;1%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-687">
                      Error rate (from 15%)
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-688">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-689">
                      &lt;2%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-690">
                      Data discrepancy rate
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center" data-id="element-691">
                    <div className="text-nrs-orange font-heading font-bold text-xl" data-id="element-692">
                      ✓ ATO
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-693">
                      Approved with sync documented
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="bg-white py-16 px-4" data-id="element-694">
        <div className="max-w-7xl mx-auto" data-id="element-695">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy text-center mb-10" data-id="element-696">
            Dedicated Resources
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-697">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-698">
              <FileTextIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-699" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-700">
                White Paper
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-701">
                "Cross-Domain Data Governance for ServiceNow in IC/DoD
                Environments" (25 pages)
              </p>
              <a href="#" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-702">
                Download White Paper →
              </a>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-703">
              <VideoIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-704" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-705">
                Webinar
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-706">
                "NIPR/JWICS Sync: Patterns, Pitfalls, and Best Practices" (60
                minutes)
              </p>
              <a href="#" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-707">
                Register for Next Session →
              </a>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-708">
              <ClipboardListIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-709" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-710">
                Self-Assessment
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-711">
                "Is Your Cross-Domain Sync at Risk?" (10-question checklist)
              </p>
              <a href="#" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-712">
                Download Checklist →
              </a>
            </motion.div>
            <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6 hover:border-nrs-blue hover:shadow-md transition-all" data-id="element-713">
              <CalendarIcon className="h-8 w-8 text-nrs-blue mb-4" data-id="element-714" />
              <div className="font-heading font-bold text-sm text-nrs-navy mb-2" data-id="element-715">
                Free Consultation
              </div>
              <p className="text-gray-600 text-xs mb-4" data-id="element-716">
                Free 30-minute cross-domain governance assessment for IC/DoD
                agencies
              </p>
              <a href="mailto:info@newriversystems.com" className="text-nrs-orange font-semibold text-xs hover:underline" data-id="element-717">
                Schedule Now →
              </a>
            </motion.div>
          </div>

          <div className="mt-10 bg-nrs-light rounded-xl p-6 border border-nrs-blue/20 text-center" data-id="element-718">
            <p className="text-nrs-navy font-semibold text-base" data-id="element-719">
              Cross-domain data governance is a massive barrier to entry for
              most ServiceNow consultants. NRS's cleared workforce and IC
              experience make us one of the few firms that can actually help
              with this problem.
            </p>
            <p className="text-gray-600 text-sm mt-2" data-id="element-720">
              If you operate ServiceNow across NIPR/JWICS, this capability alone
              justifies engaging NRS.
            </p>
          </div>
        </div>
      </section>
    </div>;
}